import type { EntityState } from '@reduxjs/toolkit';
import { createEntityAdapter } from '@reduxjs/toolkit';

import type { WatchDbmsResult } from '../relate.subscriptions';
import { WATCH_DBMSS, registerSubscription } from '../relate.subscriptions';
import type { DbmsInfo, DbmsInstallArgs, DbmsVersion } from '../relate.types';
import type { Builder } from './types';

export const dbmssAdapter = createEntityAdapter<DbmsInfo>({
  sortComparer: (a, b) => a.name.localeCompare(b.name),
});

export const dbmsEndpoints = (builder: Builder) => ({
  listDbmss: builder.query<EntityState<DbmsInfo, string>, string[] | void>({
    query: (dbmsIds) => ({
      url: 'info-dbmss',
      params: {
        dbmsIds,
        onlineCheck: true,
      },
    }),

    transformResponse(response: DbmsInfo[]) {
      return dbmssAdapter.addMany(dbmssAdapter.getInitialState(), response);
    },

    onCacheEntryAdded(arg, api) {
      return registerSubscription<WatchDbmsResult>(api, WATCH_DBMSS, ({ data }) => {
        const localDbmsStarted = data?.watchDbmss.started;
        const localDbmsStopped = data?.watchDbmss.stopped;
        const localDbmsInstalled = data?.watchDbmss.installed;
        const localDbmsUninstalled = data?.watchDbmss.uninstalled;

        api.updateCachedData((draft) => {
          if (localDbmsStarted) {
            dbmssAdapter.updateOne(draft, { id: localDbmsStarted.id, changes: localDbmsStarted });
          }

          if (localDbmsStopped) {
            dbmssAdapter.updateOne(draft, { id: localDbmsStopped.id, changes: localDbmsStopped });
          }

          if (localDbmsInstalled) {
            dbmssAdapter.addOne(draft, localDbmsInstalled);
          }

          if (localDbmsUninstalled !== undefined && localDbmsUninstalled !== null) {
            dbmssAdapter.removeOne(draft, localDbmsUninstalled);
          }
        });
      });
    },

    providesTags: (result) => (result?.ids ?? []).map((id) => ({ type: 'Dbms', id: id.toString() })),
  }),

  listNeo4jVersions: builder.query<DbmsVersion[], void>({
    query: () => 'list-dbms-versions',
  }),

  startDbms: builder.mutation<string, string>({
    query: (dbmsId) => ({
      url: 'start-dbms',
      method: 'POST',
      body: { dbmsId },
    }),
    invalidatesTags: (_res, _err, dbmsId) => [
      { type: 'Database', id: dbmsId },
      { type: 'DbmsPlugins', id: dbmsId },
    ],
  }),

  stopDbms: builder.mutation<string, string>({
    query: (dbmsId) => ({
      url: 'stop-dbms-desktop',
      method: 'POST',
      body: { dbmsId },
    }),
    invalidatesTags: (_res, _err, dbmsId) => [
      { type: 'Database', id: dbmsId },
      { type: 'DbmsPlugins', id: dbmsId },
    ],
  }),

  installDbms: builder.mutation<DbmsInfo, DbmsInstallArgs>({
    query: (args) => ({
      url: 'install-dbms-desktop',
      method: 'POST',
      body: args,
    }),
    invalidatesTags: ['Dbms', 'ProgressEvents'],
  }),

  uninstallDbms: builder.mutation<string, string>({
    query: (dbmsId) => ({
      url: 'uninstall-dbms',
      method: 'POST',
      body: {
        name: dbmsId,
      },
    }),
    invalidatesTags: ['Dbms'],
  }),

  upgradeDbms: builder.mutation<DbmsInfo, { dbmsId: string; targetVersion: string }>({
    query: ({ dbmsId, targetVersion }) => ({
      url: 'upgrade-dbms',
      method: 'POST',
      body: {
        dbmsId,
        version: targetVersion,
      },
    }),

    invalidatesTags: ['Dbms'],
  }),

  cloneDbms: builder.mutation<DbmsInfo, { dbmsId: string; newName: string }>({
    query: ({ dbmsId, newName }) => ({
      url: 'clone-dbms',
      method: 'POST',
      body: {
        dbmsId,
        newName,
      },
    }),
    invalidatesTags: ['Dbms'],
  }),

  updateDbmsName: builder.mutation<unknown, { dbmsId: string; newName: string }>({
    query: ({ dbmsId, newName }) => ({
      url: 'update-dbms-name',
      method: 'POST',
      body: {
        dbmsId,
        newName,
      },
    }),
    invalidatesTags: ['Dbms'],
  }),
});
